import {
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Input,
    SimpleGrid,
    Spinner,
    Switch,
    Text,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import {useEffect, useState} from "react";
import {fetchSchoolMetaDetail, updateSchoolMeta} from "../Services";

const numToStr = (v) => (v === null || v === undefined ? "" : String(v));

// Map the raw meta response to the editable form shape (all values as the
// strings/booleans the inputs use).
const metaToForm = (meta = {}) => ({
    country: meta.country ?? "",
    countryCode: meta.countryCode ?? "",
    language: meta.language ?? "",
    timezone: meta.timezone ?? "",
    playTime: numToStr(meta.playTime),
    playedDays: numToStr(meta.playedDays),
    geoFenceEnabled: !!meta.geoFenceEnabled,
    geoFenceRadiusMeters: numToStr(meta.geoFenceRadiusMeters),
    geoFenceLatitude: numToStr(meta.geoFenceLatitude),
    geoFenceLongitude: numToStr(meta.geoFenceLongitude),
});

const strOrNull = (v) => {
    const t = String(v ?? "").trim();
    return t === "" ? null : t;
};
const intOrNull = (v) => {
    const t = String(v ?? "").trim();
    return t === "" ? null : parseInt(t, 10);
};
const floatOrNull = (v) => {
    const t = String(v ?? "").trim();
    return t === "" ? null : parseFloat(t);
};

// Build the request body sent to PUT /school/meta/{id}.
const formToPayload = (form, metaId, schoolId) => ({
    id: metaId,
    schoolId: schoolId,
    country: strOrNull(form.country),
    countryCode: strOrNull(form.countryCode),
    language: strOrNull(form.language),
    timezone: strOrNull(form.timezone),
    playTime: intOrNull(form.playTime),
    playedDays: intOrNull(form.playedDays),
    geoFenceEnabled: form.geoFenceEnabled,
    geoFenceRadiusMeters: floatOrNull(form.geoFenceRadiusMeters),
    geoFenceLatitude: floatOrNull(form.geoFenceLatitude),
    geoFenceLongitude: floatOrNull(form.geoFenceLongitude),
});

const SchoolSetupDetail = ({selectedSchool}) => {
    const toast = useToast();
    const textColor = useColorModeValue("gray.700", "white");
    const cardBg = useColorModeValue("white", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");

    const [metaId, setMetaId] = useState(null);
    const [form, setForm] = useState(metaToForm());
    const [baseline, setBaseline] = useState(metaToForm());
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchSchoolMetadata = async () => {
            setIsLoading(true);
            try {
                const response = await fetchSchoolMetaDetail(selectedSchool);
                if (response.status === 200) {
                    const meta = response.data || {};
                    setMetaId(meta.id ?? null);
                    setForm(metaToForm(meta));
                    setBaseline(metaToForm(meta));
                }
            } catch (e) {
                console.error("Error fetching school meta detail:", e);
                toast({
                    title: "Failed to load setup",
                    description: "Could not load the school setup details.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchSchoolMetadata();
    }, [selectedSchool, toast]);

    const handleChange = (field, value) => {
        setForm((prev) => ({...prev, [field]: value}));
    };

    const isDirty = JSON.stringify(form) !== JSON.stringify(baseline);

    const handleCancel = () => {
        setForm(baseline);
    };

    const handleSubmit = async () => {
        if (metaId == null) {
            return;
        }
        setIsSaving(true);
        try {
            const payload = formToPayload(form, metaId, selectedSchool);
            const response = await updateSchoolMeta(selectedSchool, payload);
            if (response.status === 200 || response.status === 201) {
                setBaseline(form);
                toast({
                    title: "Setup updated",
                    description: "School setup was updated successfully.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (e) {
            console.error("Error updating school meta:", e);
            toast({
                title: "Update failed",
                description: "Could not update the school setup. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Flex justify="center" align="center" py="60px" gap="12px" w="100%">
                <Spinner color="blue.400" size="md"/>
                <Text color="gray.500" fontSize="sm">Loading setup...</Text>
            </Flex>
        );
    }

    const geoDisabled = !form.geoFenceEnabled;

    return (
        <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="15px"
            p={{base: "16px", md: "24px"}}
            w="100%"
        >
            <Text fontSize="lg" fontWeight="bold" color={textColor} mb="4px">
                School Setup
            </Text>
            <Text fontSize="sm" color="gray.400" mb="20px">
                Update the setup details for this school.
            </Text>

            <SimpleGrid columns={{base: 1, md: 2}} spacing="20px">
                <FormControl>
                    <FormLabel fontSize="sm">Country</FormLabel>
                    <Input
                        value={form.country}
                        onChange={(e) => handleChange("country", e.target.value)}
                        placeholder="e.g. NP"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel fontSize="sm">Country Code</FormLabel>
                    <Input
                        value={form.countryCode}
                        onChange={(e) => handleChange("countryCode", e.target.value)}
                        placeholder="e.g. +977"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel fontSize="sm">Language</FormLabel>
                    <Input
                        value={form.language}
                        onChange={(e) => handleChange("language", e.target.value)}
                        placeholder="e.g. English"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel fontSize="sm">Timezone</FormLabel>
                    <Input
                        value={form.timezone}
                        onChange={(e) => handleChange("timezone", e.target.value)}
                        placeholder="e.g. Asia/Kathmandu"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel fontSize="sm">Bell Play Time (seconds)</FormLabel>
                    <Input
                        type="number"
                        value={form.playTime}
                        onChange={(e) => handleChange("playTime", e.target.value)}
                        placeholder="e.g. 60"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel fontSize="sm">Played Days</FormLabel>
                    <Input
                        type="number"
                        value={form.playedDays}
                        onChange={(e) => handleChange("playedDays", e.target.value)}
                        placeholder="e.g. 1"
                    />
                </FormControl>
            </SimpleGrid>

            <Divider my="24px"/>

            <Flex align="center" justify="space-between" mb="16px">
                <Box>
                    <Text fontSize="md" fontWeight="semibold" color={textColor}>
                        Geo-fencing
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                        Restrict bell playback to a location radius.
                    </Text>
                </Box>
                <Switch
                    isChecked={form.geoFenceEnabled}
                    onChange={(e) => handleChange("geoFenceEnabled", e.target.checked)}
                    colorScheme="blue"
                />
            </Flex>

            <SimpleGrid columns={{base: 1, md: 3}} spacing="20px">
                <FormControl isDisabled={geoDisabled}>
                    <FormLabel fontSize="sm">Radius (meters)</FormLabel>
                    <Input
                        type="number"
                        value={form.geoFenceRadiusMeters}
                        onChange={(e) => handleChange("geoFenceRadiusMeters", e.target.value)}
                        placeholder="e.g. 50"
                    />
                </FormControl>

                <FormControl isDisabled={geoDisabled}>
                    <FormLabel fontSize="sm">Latitude</FormLabel>
                    <Input
                        type="number"
                        value={form.geoFenceLatitude}
                        onChange={(e) => handleChange("geoFenceLatitude", e.target.value)}
                        placeholder="e.g. 27.7172"
                    />
                </FormControl>

                <FormControl isDisabled={geoDisabled}>
                    <FormLabel fontSize="sm">Longitude</FormLabel>
                    <Input
                        type="number"
                        value={form.geoFenceLongitude}
                        onChange={(e) => handleChange("geoFenceLongitude", e.target.value)}
                        placeholder="e.g. 85.3240"
                    />
                </FormControl>
            </SimpleGrid>

            <Flex justify="flex-end" gap="12px" mt="28px">
                <Button
                    variant="outline"
                    onClick={handleCancel}
                    isDisabled={!isDirty || isSaving}
                >
                    Cancel
                </Button>
                <Button
                    colorScheme="blue"
                    onClick={handleSubmit}
                    isLoading={isSaving}
                    loadingText="Saving"
                    isDisabled={!isDirty || metaId == null}
                >
                    Submit
                </Button>
            </Flex>
        </Box>
    );
};

export default SchoolSetupDetail;
